import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Baby, Search, Save, Check, DoorOpen, AlertTriangle } from "lucide-react";

export default function AssignChildrenDialog({ open, onClose, teacher }) {
  const [children, setChildren] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roomInfo, setRoomInfo] = useState(null); // {has_room, room_number, capacity, other_teachers_count, other_teachers_children_ids}

  useEffect(() => {
    if (!open || !teacher) return;
    setLoading(true);
    (async () => {
      try {
        const [all, mine, ri] = await Promise.all([
          api.get("/children"),
          api.get(`/users/${teacher.id}/assigned-children`),
          api.get(`/admin/teacher-room-info/${teacher.id}`),
        ]);
        setChildren(all.data || []);
        setSelected(new Set((mine.data || []).map((c) => c.id)));
        setRoomInfo(ri.data || null);
      } catch (e) { toast.error(extractError(e)); }
      finally { setLoading(false); }
    })();
  }, [open, teacher]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return children;
    return children.filter((c) => (c.name || "").toLowerCase().includes(t));
  }, [q, children]);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  // Projected room population = union(other teachers' children in this room, currently-selected)
  const projected = useMemo(() => {
    if (!roomInfo?.has_room) return null;
    const cap = Number(roomInfo.capacity) || 0;
    const others = new Set(roomInfo.other_teachers_children_ids || []);
    const union = new Set(others);
    selected.forEach((id) => union.add(id));
    const count = union.size;
    const overflow = cap > 0 && count > cap;
    const nearly = cap > 0 && !overflow && count / cap >= 0.85;
    const pct = cap > 0 ? Math.min(100, Math.round((count / cap) * 100)) : 0;
    return { cap, count, overflow, nearly, pct, others_count: others.size };
  }, [roomInfo, selected]);

  const save = async () => {
    if (!teacher) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/users/${teacher.id}/assign-children`, { child_ids: Array.from(selected) });
      toast.success(`تم إسناد ${selected.size} طفل للمعلم`);
      if (data?.warning?.code === "room_over_capacity") {
        toast.warning(data.warning.message, { duration: 8000 });
      }
      onClose();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  if (!teacher) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="assign-children-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black flex items-center gap-2">
            <Baby className="w-6 h-6 text-emerald-600" /> إسناد أطفال إلى — {teacher.name}
          </DialogTitle>
        </DialogHeader>

        {/* Room Capacity Meter */}
        {roomInfo?.has_room && projected && (
          <div
            data-testid="room-capacity-meter"
            className={`rounded-2xl border-2 p-4 mb-4 transition-all ${
              projected.overflow
                ? "border-rose-300 bg-rose-50 ring-4 ring-rose-200 animate-pulse"
                : projected.nearly
                ? "border-amber-300 bg-amber-50"
                : "border-emerald-200 bg-emerald-50"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <DoorOpen className={`w-5 h-5 ${projected.overflow ? "text-rose-600" : projected.nearly ? "text-amber-600" : "text-emerald-600"}`} />
                <span className="font-black text-sm text-slate-800">
                  قاعة {roomInfo.room_number}
                </span>
                {roomInfo.other_teachers_count > 0 && (
                  <span className="text-xs text-slate-500 font-bold">
                    (يشاركها {roomInfo.other_teachers_count} معلم آخر)
                  </span>
                )}
              </div>
              <div className={`text-sm font-black ${projected.overflow ? "text-rose-700" : projected.nearly ? "text-amber-700" : "text-emerald-700"}`} data-testid="capacity-count">
                <span dir="ltr" style={{unicodeBidi: "embed"}}>
                  {projected.count}
                  {projected.cap > 0 ? ` / ${projected.cap}` : ""}
                </span>
                {projected.cap === 0 && <span className="text-slate-500 font-bold"> طفل (بلا سعة محددة)</span>}
              </div>
            </div>

            {projected.cap > 0 && (
              <div className="w-full h-2.5 rounded-full bg-white/70 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    projected.overflow
                      ? "bg-gradient-to-l from-rose-500 to-red-600"
                      : projected.nearly
                      ? "bg-gradient-to-l from-amber-400 to-orange-500"
                      : "bg-gradient-to-l from-emerald-400 to-teal-500"
                  }`}
                  style={{ width: `${projected.pct}%` }}
                  data-testid="capacity-bar"
                />
              </div>
            )}

            <div className="mt-2 text-xs font-bold flex items-center gap-1.5">
              {projected.overflow ? (
                <>
                  <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
                  <span className="text-rose-700" data-testid="capacity-warning-text">
                    ⚠️ سيتم تجاوز السعة بـ {projected.count - projected.cap} طفل. يُنصح بإعادة توزيع أو رفع السعة.
                  </span>
                </>
              ) : projected.nearly ? (
                <span className="text-amber-700">
                  اقتربت من الحد الأقصى — تبقّى {projected.cap - projected.count} مقاعد
                </span>
              ) : projected.cap > 0 ? (
                <span className="text-emerald-700">
                  آمن — {projected.cap - projected.count} مقعد متاح في القاعة
                </span>
              ) : (
                <span className="text-slate-500">
                  لم تُحدَّد سعة قصوى لهذه القاعة — يمكن ضبطها من صفحة "خريطة القاعات"
                </span>
              )}
            </div>

            {projected.others_count > 0 && (
              <div className="mt-1 text-[11px] text-slate-500">
                منهم {projected.others_count} طفل مسند لمعلمين آخرين في نفس القاعة (خارج نطاق هذا التعديل)
              </div>
            )}
          </div>
        )}

        <div className="text-xs text-slate-500 mb-3">
          حدّد الأطفال الذين سيتمكن هذا المعلم من رؤيتهم وعمل تقييمات لهم. إزالة العلامة تُلغي الإسناد.
        </div>
        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input dir="rtl" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث باسم الطفل..." className="pr-9" data-testid="assign-search" />
        </div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-black text-emerald-700 bg-emerald-50 rounded-full px-3 py-1">
            محدّد: {selected.size} من {children.length}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set(filtered.map((c) => c.id)))} data-testid="assign-select-all">تحديد المرئي</Button>
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} data-testid="assign-clear">مسح</Button>
          </div>
        </div>
        <div className="space-y-1.5 max-h-[45vh] overflow-y-auto pr-1">
          {loading ? (
            <div className="text-center py-10 text-slate-400 text-sm">جاري التحميل...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">لا توجد نتائج</div>
          ) : filtered.map((c) => {
            const on = selected.has(c.id);
            return (
              <label
                key={c.id}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${on ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200 bg-white hover:border-emerald-200"}`}
                data-testid={`assign-child-${c.id}`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggle(c.id)}
                  className="w-4 h-4 accent-emerald-600"
                />
                <div className="flex-1 font-bold text-sm text-slate-800">{c.name}</div>
                {on && <Check className="w-4 h-4 text-emerald-600" />}
              </label>
            );
          })}
        </div>
        <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-slate-100">
          <Button variant="outline" onClick={onClose} disabled={saving}>إلغاء</Button>
          <Button onClick={save} disabled={saving} className="gap-2 bg-gradient-to-l from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700" data-testid="assign-save-btn">
            <Save className="w-4 h-4" /> {saving ? "جارٍ الحفظ..." : `حفظ الإسناد (${selected.size})`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
